import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

/**
 * Asigna fotos seleccionadas a un proveedor + Materialoteca.
 * Flujo: 1) proveedor  2) crear material o enlazar uno existente.
 */
const FeriaLinkMaterialotecaDialog = ({
  open,
  onClose,
  onConfirm,
  materiales = [],
  proveedores = [],
  busy = false,
  selectedCount = 0,
}) => {
  const [mode, setMode] = useState("create");
  const [materialId, setMaterialId] = useState(null);
  const [nombre, setNombre] = useState("");
  const [tipoMaterial, setTipoMaterial] = useState(1);
  const [proveedorKey, setProveedorKey] = useState(null);
  const [pedir, setPedir] = useState(false);
  const [error, setError] = useState("");

  const materialOptions = useMemo(
    () =>
      (materiales || [])
        .map((m) => ({
          id: Number(m.id_Materialoteca || m.material?.Id),
          label:
            m.material?.NombreMaterial ||
            m.NombreMaterial ||
            `#${m.id_Materialoteca}`,
          row: m,
        }))
        .filter((o) => Number.isFinite(o.id) && o.id > 0),
    [materiales],
  );

  const proveedorOptions = useMemo(
    () =>
      (proveedores || []).map((p) => {
        const name =
          p.proveedor?.N_PROVEEDOR ||
          p.fabrica?.NombreFabrica ||
          p.Nombre ||
          "Proveedor";
        return {
          key: `fp-${p.id_FeriaProveedor}`,
          label: name,
          CODPROV: p.CODPROV || p.proveedor?.CODPROV || null,
          id_Fabrica: p.id_Fabrica || p.fabrica?.id_Fabrica || null,
        };
      }),
    [proveedores],
  );

  useEffect(() => {
    if (!open) return;
    setMode("create");
    setMaterialId(null);
    setNombre("");
    setTipoMaterial(1);
    setProveedorKey(proveedorOptions[0]?.key || null);
    setPedir(false);
    setError("");
  }, [open, proveedorOptions]);

  const reset = () => {
    setMode("create");
    setMaterialId(null);
    setNombre("");
    setTipoMaterial(1);
    setProveedorKey(null);
    setPedir(false);
    setError("");
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const handleConfirm = async () => {
    setError("");
    const prov = proveedorOptions.find((p) => p.key === proveedorKey);
    if (!prov) {
      setError("Elige un proveedor. Si no hay ninguno, créalo a la derecha.");
      return;
    }

    if (mode === "existing") {
      if (!materialId) {
        setError("Elige un material de la edición.");
        return;
      }
      await onConfirm?.({
        id_Materialoteca: materialId,
        pedir,
        CODPROV: prov.CODPROV || undefined,
        id_Fabrica: prov.id_Fabrica || undefined,
      });
    } else {
      const trimmed = String(nombre || "").trim();
      if (!trimmed) {
        setError("Pon un nombre al material (ej. COTTON/LINEN).");
        return;
      }
      const tipo = Number(tipoMaterial);
      if (!Number.isFinite(tipo) || tipo < 1) {
        setError("Tipo de material inválido.");
        return;
      }
      await onConfirm?.({
        create: {
          NombreMaterial: trimmed,
          TipoMaterial: tipo,
        },
        pedir,
        CODPROV: prov.CODPROV || undefined,
        id_Fabrica: prov.id_Fabrica || undefined,
      });
    }
    reset();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Asignar fotos a proveedor
        {selectedCount > 0 ? (
          <Typography variant="body2" color="text.secondary" component="div">
            {selectedCount} foto{selectedCount === 1 ? "" : "s"} → Materialoteca
            + proveedor
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {!proveedorOptions.length ? (
            <Alert severity="warning">
              Primero añade un proveedor en el panel de la derecha («Añadir
              proveedor»). Luego vuelve aquí.
            </Alert>
          ) : (
            <Autocomplete
              options={proveedorOptions}
              value={
                proveedorOptions.find((p) => p.key === proveedorKey) || null
              }
              onChange={(_, v) => setProveedorKey(v?.key || null)}
              getOptionLabel={(o) => o.label || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Proveedor *"
                  size="small"
                  helperText="Las fotos quedarán ligadas a este proveedor"
                />
              )}
            />
          )}

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant={mode === "create" ? "contained" : "outlined"}
              onClick={() => setMode("create")}
              sx={{ textTransform: "none" }}
            >
              Crear material nuevo
            </Button>
            <Button
              size="small"
              variant={mode === "existing" ? "contained" : "outlined"}
              onClick={() => setMode("existing")}
              sx={{ textTransform: "none" }}
              disabled={!materialOptions.length}
            >
              Usar material existente
            </Button>
          </Stack>

          {mode === "existing" ? (
            <Autocomplete
              options={materialOptions}
              value={materialOptions.find((o) => o.id === materialId) || null}
              onChange={(_, v) => setMaterialId(v?.id || null)}
              getOptionLabel={(o) => o.label || ""}
              renderInput={(params) => (
                <TextField {...params} label="Material" size="small" />
              )}
            />
          ) : (
            <Stack spacing={1.5}>
              <TextField
                label="Nombre del material *"
                size="small"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej. COTTON/LINEN 132g"
                fullWidth
              />
              <TextField
                select
                label="Tipo material (id)"
                size="small"
                value={tipoMaterial}
                onChange={(e) => setTipoMaterial(Number(e.target.value) || 1)}
                helperText="Id de subgrupo / tipo en Materialoteca"
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={pedir}
                onChange={(e) => setPedir(e.target.checked)}
              />
            }
            label="Pedir muestra"
          />

          {error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={busy}
          sx={{ textTransform: "none" }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={busy || !proveedorOptions.length}
          sx={{ textTransform: "none" }}
        >
          Asignar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeriaLinkMaterialotecaDialog;
